import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import { Trophy, X } from "lucide-react";
import { getSocket } from "@/lib/socket";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DISMISS_KEY = "samtwo_position_banner_dismissed";
const dismissed = () => { try { return JSON.parse(localStorage.getItem(DISMISS_KEY) || "[]"); } catch { return []; } };

// Sitewide banner for an open 1-100 spot. Reads the listings that are open NOW on load
// (so a visitor who arrives after the announcement still sees it) and also reacts to the
// live "position_open" broadcast. Dismiss hides that position on this device.
export const PositionOpenBanner = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [evt, setEvt] = useState(null);

  useEffect(() => {
    let alive = true;
    axios.get(`${API}/positions/open`).then(({ data }) => {
      const gone = dismissed();
      const l = (data?.listings || []).find((x) => !gone.includes(x.position));
      if (alive && l) setEvt((cur) => cur || { position: l.position, price: l.price });
    }).catch(() => {});
    const socket = getSocket();
    const onOpen = (p) => { if (p && !dismissed().includes(p.position)) setEvt(p); };
    socket.on("position_open", onOpen);
    return () => { alive = false; socket.off("position_open", onOpen); };
  }, []);

  if (!evt) return null;
  const url = `/pricing?claim=${evt.position}`;
  const go = () => { setEvt(null); navigate(url); };
  const close = () => {
    try { localStorage.setItem(DISMISS_KEY, JSON.stringify([...dismissed(), evt.position].slice(-100))); } catch { /* private mode */ }
    setEvt(null);
  };

  return (
    <div className="fixed bottom-4 inset-x-0 z-[97] flex justify-center px-4 pointer-events-none" data-testid="position-open-banner-wrap">
    <AnimatePresence>
      <motion.div
        initial={{ y: 90, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 90, opacity: 0 }}
        transition={{ type: "spring", stiffness: 300, damping: 28 }}
        data-testid="position-open-banner"
        className="pointer-events-auto flex items-center gap-3 px-4 py-3 rounded-2xl w-full max-w-md"
        style={{ background: "var(--bg-surface)", border: "1.5px solid #f5b301", boxShadow: "0 12px 40px rgba(0,0,0,0.22)" }}
      >
        <Trophy size={20} style={{ color: "#f5b301" }} className="flex-shrink-0" />
        <p className="text-sm min-w-0 flex-1" style={{ color: "var(--text-primary)" }} data-testid="position-open-banner-text">
          {evt.price != null ? t("positions.bannerOpen", { position: evt.position, price: evt.price }) : t("positions.bannerOpenNoPrice", { position: evt.position })}
        </p>
        <button onClick={go} data-testid="position-open-banner-claim"
          className="flex-shrink-0 inline-flex items-center gap-1 px-3 py-2 rounded-full text-xs font-bold"
          style={{ background: "var(--accent-primary)", color: "#fff" }}>
          {t("positions.bannerClaim")}
        </button>
        <button onClick={close} data-testid="position-open-banner-dismiss"
          className="flex-shrink-0 p-1 rounded-full opacity-60 hover:opacity-100" style={{ color: "var(--text-secondary)" }} aria-label={t("positions.bannerDismiss")}>
          <X size={15} />
        </button>
      </motion.div>
    </AnimatePresence>
    </div>
  );
};

export default PositionOpenBanner;
